/**
 * Renders a server-provided snippet that contains `<mark>…</mark>` markers
 * around matched terms — WITHOUT `dangerouslySetInnerHTML`.
 *
 * The search API guarantees the snippet is HTML-escaped and only ever
 * contains `<mark>` tags (see packages/shared search schema). To stay safe
 * even if that guarantee is ever violated, we never inject raw HTML: we parse
 * out the `<mark>` spans ourselves, decode the handful of HTML entities the
 * escaper can emit, and render real React `<mark>` elements around plain
 * text. Any other tag is treated as literal text, so injected markup can
 * never execute.
 */
import Box from "@mui/material/Box";
import { Fragment, type ReactElement } from "react";

const ENTITY_MAP: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&#x27;": "'",
};

function decodeEntities(text: string): string {
  return text.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (m) => ENTITY_MAP[m] ?? m);
}

/** Split on `<mark>…</mark>` pairs; everything else is literal (escaped) text. */
const MARK_RE = /<mark>([\s\S]*?)<\/mark>/g;

export interface HighlightedSnippetProps {
  snippet: string;
  /** Optional component override for the wrapper (defaults to a span). */
  component?: React.ElementType;
}

export function HighlightedSnippet({
  snippet,
  component = "span",
}: HighlightedSnippetProps): ReactElement {
  const parts: ReactElement[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  MARK_RE.lastIndex = 0;
  while ((match = MARK_RE.exec(snippet)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <Fragment key={key++}>{decodeEntities(snippet.slice(lastIndex, match.index))}</Fragment>,
      );
    }
    parts.push(
      <Box
        component="mark"
        key={key++}
        sx={{
          bgcolor: "warning.light",
          color: "text.primary",
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {decodeEntities(match[1] ?? "")}
      </Box>,
    );
    lastIndex = MARK_RE.lastIndex;
  }
  if (lastIndex < snippet.length) {
    parts.push(<Fragment key={key++}>{decodeEntities(snippet.slice(lastIndex))}</Fragment>);
  }

  return <Box component={component}>{parts}</Box>;
}
